// Pure presentation helpers for the process monitor's Agents tab, extracted from
// AgentMonitorView so the ordering and labelling are unit-testable without
// rendering the component (which pulls a modal/confirm/API). Same reason
// processMonitorGroups.js exists.

// Row kinds, mirroring app/core/llm_meter.py's constants. Change one, change both.
export const KIND_LABELS = {
  orchestrator: 'Workflow assistant',
  subagent: 'Module assistant',
  replay: 'Quality Check replay',
  helper: 'Assistant helper',
};

// Row states, mirroring app/services/agent_monitor.py. The backend sends the
// display string directly (it is the one vocabulary both sides reason about), so
// this map exists for the CSS class, not to re-translate the words.
export const STATE_CLASSES = {
  'calling model': 'calling',
  'needs input': 'needs-input',
  'waiting on job': 'waiting',
  queued: 'queued',
  running: 'running',
};

/**
 * Human label for an agent row. A module assistant names its module, because
 * "Module assistant" alone is useless when three of them are running: the tile
 * label is the whole point of the row.
 */
export function agentKindLabel(agent) {
  const base = KIND_LABELS[agent?.kind] || agent?.kind || 'Assistant';
  if (agent?.kind === 'subagent') {
    const which = agent.nodeLabel || agent.tileType;
    return which ? `${which} assistant` : base;
  }
  return base;
}

/** CSS suffix for a row's state pill; unknown states fall back to a neutral class. */
export function agentStateClass(state) {
  return STATE_CLASSES[state] || 'running';
}

/**
 * Compact token count. Tokens run to the millions over a day, and a raw
 * 1348201 in a table cell is unreadable — the magnitude is what a reader is
 * actually comparing.
 */
export function formatTokens(n) {
  const v = Number(n);
  if (!isFinite(v) || v <= 0) return '0';
  if (v < 1000) return String(Math.round(v));
  if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)}k`;
  return `${(v / 1_000_000).toFixed(1)}M`;
}

/** `in / out` for a row or the day meter. */
export function formatTokenPair(tokensIn, tokensOut) {
  return `${formatTokens(tokensIn)} / ${formatTokens(tokensOut)}`;
}

const KIND_RANK = { orchestrator: 0, subagent: 1, replay: 2, helper: 3 };

/**
 * Order the rows: the caller's own first (matching the Processes tab), then
 * grouped by run so a run's modules sit under their orchestrator, then
 * orchestrator-before-subagent within a run, then by start time.
 *
 * Grouping by `runId` is what keeps a run's rows adjacent — sorting by state or
 * start time alone would interleave two users' runs and the reader would have to
 * correlate them by project name.
 */
export function sortAgents(agents, currentUserId) {
  const mine = (a) => (a.ownerUserId === currentUserId ? 0 : 1);
  return [...(agents || [])].sort((a, b) => {
    const d = mine(a) - mine(b);
    if (d !== 0) return d;
    const ra = a.runId || '';
    const rb = b.runId || '';
    if (ra !== rb) return ra < rb ? -1 : 1;
    const ka = KIND_RANK[a.kind] ?? 9;
    const kb = KIND_RANK[b.kind] ?? 9;
    if (ka !== kb) return ka - kb;
    return String(a.startedAt || '').localeCompare(String(b.startedAt || ''));
  });
}

/** How many of these rows the caller owns — drives "Stop all mine (N)". */
export function countStoppableMine(agents, currentUserId) {
  return (agents || []).filter((a) => a.stoppable && a.ownerUserId === currentUserId).length;
}
